/**
 * Quick task 260819-kq3 — `#` task-reference picker.
 *
 * Modeled on MentionPicker (Phase 25): a CONTROLLED satellite of a parent
 * <textarea>. The parent owns the textarea ref + value + onChange. The picker:
 *  1. Watches the textarea's value + caret index for a `#partial` token.
 *  2. Filters candidates CLIENT-SIDE from the already-cached board query
 *     (`useBoard(projectId, audience)`) — no new endpoint. Audience scoping
 *     is inherited: the external board only ever contains external-visible
 *     tasks, so an external composer can never leak internal task names.
 *  3. On selection, calls onInsert with { code, anchorIndex, partialEnd };
 *     the parent inserts the PLAIN-TEXT token `#CODE ` (no hidden encoding —
 *     see taskRefEncoding.ts).
 *
 * Trigger discipline mirrors MentionPicker's `@` rules, so the two pickers
 * sharing one textarea are mutually exclusive: a `#` inside an active
 * `@partial` (or vice versa) makes the other picker's partial contain a
 * non-token character and closes it.
 */
import { useState, useEffect, useCallback, useMemo, type RefObject } from 'react';
import { useBoard } from '../../hooks/useBoard';
import type { TaskCard } from '../../services/api/board';
import { getCaretCoordinates } from './caretCoords';

const PICKER_LIMIT = 8;

export interface TaskRefInsertion {
  /** Ticket code of the selected task (e.g. `MKS-138`). */
  code: string;
  /** Inclusive char index of the `#` trigger. */
  anchorIndex: number;
  /** Exclusive char index where the partial ends (caret position when the row was selected). */
  partialEnd: number;
}

export interface TaskRefPickerProps {
  /** The textarea this picker watches. Parent passes the ref. */
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  /** Current textarea value (controlled). */
  value: string;
  /** Project whose board supplies the candidates. */
  projectId: number | null;
  /** Internal vs external composer — selects the audience-correct board query. */
  audience: 'internal' | 'external';
  /** Task the composer belongs to — excluded from candidates (no self-reference). */
  excludeTaskId?: number;
  /** Called when the user picks a row. Parent rewrites the textarea value. */
  onInsert: (insertion: TaskRefInsertion) => void;
}

interface OpenState {
  /** Inclusive char index of the `#` trigger. */
  anchorIndex: number;
  /** Substring typed after `#` so far (candidate filter query). */
  partial: string;
  /** Highlighted row index (0-based). */
  highlight: number;
  /** Caret coords at the `#` position (anchors the popover). */
  coords: { top: number; left: number; height: number };
}

export function TaskRefPicker({
  textareaRef,
  value,
  projectId,
  audience,
  excludeTaskId,
  onInsert,
}: TaskRefPickerProps) {
  const [open, setOpen] = useState<OpenState | null>(null);

  // Board query is shared-cache with the kanban board / ticket page the
  // composer sits on, so this mounts an extra observer, not an extra fetch.
  const { data: board } = useBoard(projectId ? String(projectId) : '', audience);

  const candidates = useMemo(() => {
    if (!open || !board) return [];
    const q = open.partial.toLowerCase();
    const all: TaskCard[] = board.columns.flatMap((col) => col.tasks);
    const referenceable = all.filter(
      (t) => typeof t.code === 'string' && t.code.length > 0 && t.id !== excludeTaskId,
    );
    const matches = q
      ? referenceable.filter(
          (t) =>
            (t.code as string).toLowerCase().includes(q) || t.name.toLowerCase().includes(q),
        )
      : referenceable;
    // Code prefix matches first, then code substring, then name-only matches.
    const rank = (t: TaskCard): number => {
      const code = (t.code as string).toLowerCase();
      if (q && code.startsWith(q)) return 0;
      if (q && code.includes(q)) return 1;
      return 2;
    };
    return [...matches].sort((a, b) => rank(a) - rank(b) || b.id - a.id);
  }, [open, board, excludeTaskId]);

  const visibleRows = useMemo(() => candidates.slice(0, PICKER_LIMIT), [candidates]);
  const hasMore = candidates.length > PICKER_LIMIT;

  const close = useCallback(() => setOpen(null), []);

  // Watch the textarea — figure out whether we're inside a `#partial` token.
  // Mirrors MentionPicker's trigger conditions:
  //   - The most recent `#` before the caret has no whitespace/comma between
  //     it and the caret (whitespace or comma after `#` cancels).
  //   - The `#` is at the start of the buffer OR preceded by whitespace.
  const recomputeOpen = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const caret = ta.selectionStart ?? value.length;
    const upto = value.substring(0, caret);
    const hashIdx = upto.lastIndexOf('#');
    if (hashIdx === -1) {
      setOpen(null);
      return;
    }
    const prevChar = hashIdx === 0 ? ' ' : upto.charAt(hashIdx - 1);
    if (!/\s/.test(prevChar)) {
      setOpen(null);
      return;
    }
    const partial = upto.substring(hashIdx + 1);
    if (/[\s,]/.test(partial)) {
      setOpen(null);
      return;
    }
    const coords = getCaretCoordinates(ta, hashIdx);
    setOpen((prev) => ({
      anchorIndex: hashIdx,
      partial,
      // Preserve the user's highlight on the same `#`-trigger run; reset to 0
      // when the trigger position changes (a new `#` was started).
      highlight: prev && prev.anchorIndex === hashIdx ? prev.highlight : 0,
      coords,
    }));
  }, [textareaRef, value]);

  useEffect(() => {
    recomputeOpen();
  }, [recomputeOpen]);

  // Clamp the highlight if the visible row count shrinks below it.
  useEffect(() => {
    if (!open) return;
    if (visibleRows.length === 0) return;
    if (open.highlight >= visibleRows.length) {
      setOpen((prev) => (prev ? { ...prev, highlight: visibleRows.length - 1 } : prev));
    }
  }, [open, visibleRows.length]);

  // Keyboard navigation — bound to the textarea's own keydown so the picker
  // can preventDefault on arrow keys / Enter / Tab without interfering with
  // the textarea's own input handling.
  useEffect(() => {
    if (!open) return;
    const ta = textareaRef.current;
    if (!ta) return;

    function handleKey(e: KeyboardEvent) {
      if (!open) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen((prev) =>
          prev
            ? {
                ...prev,
                highlight: Math.min(prev.highlight + 1, Math.max(0, visibleRows.length - 1)),
              }
            : prev,
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen((prev) =>
          prev ? { ...prev, highlight: Math.max(prev.highlight - 1, 0) } : prev,
        );
        return;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && visibleRows.length > 0) {
        e.preventDefault();
        const row = visibleRows[open.highlight] ?? visibleRows[0];
        if (row) selectRow(row);
        return;
      }
    }

    ta.addEventListener('keydown', handleKey);
    return () => ta.removeEventListener('keydown', handleKey);
    // selectRow / close are stable enough — re-bind only when open or the
    // visible candidate set changes so the closure has fresh references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, visibleRows, textareaRef]);

  function selectRow(row: TaskCard) {
    if (!open || !row.code) return;
    const partialEnd = open.anchorIndex + 1 + open.partial.length;
    onInsert({ code: row.code, anchorIndex: open.anchorIndex, partialEnd });
    close();
  }

  if (!open) return null;

  return (
    <div
      role="listbox"
      aria-label="Task reference candidates"
      className="absolute z-30 min-w-[260px] max-w-[360px] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
      style={{ top: open.coords.top + open.coords.height + 4, left: open.coords.left }}
    >
      {visibleRows.length === 0 && (
        <div className="px-3 py-2 text-sm font-medium text-gray-400 italic">No matching tasks</div>
      )}
      {visibleRows.map((row, idx) => (
        <button
          key={row.id}
          type="button"
          role="option"
          aria-selected={idx === open.highlight}
          onMouseDown={(e) => {
            // mousedown to fire BEFORE the textarea blur cancels the selection.
            e.preventDefault();
            selectRow(row);
          }}
          className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm ${
            idx === open.highlight ? 'bg-primary-50' : 'bg-white hover:bg-gray-50'
          }`}
        >
          <span className="shrink-0 font-mono text-xs font-semibold text-primary-700 bg-primary-50 px-1.5 py-0.5 rounded">
            {row.code}
          </span>
          <span className="flex-1 min-w-0 truncate font-medium text-gray-900">{row.name}</span>
        </button>
      ))}
      {hasMore && (
        <div className="px-3 py-2 text-xs font-medium text-gray-400 italic border-t border-gray-100">
          +{candidates.length - PICKER_LIMIT} more — keep typing
        </div>
      )}
    </div>
  );
}
